import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Handles the return leg of a hosted card payment (N-Genius paypage — real or
// mock). Two modes:
//
//   LEGACY (query-param) mode — no options.refOverride: on mount, if the URL
//   carries ?payment=return&ref=<order_ref>, the hook captures the ref and
//   RETAINED DELIBERATELY (audited 2026-07-18). No backend route emits
//   ?payment=return any more — every checkout now registers a query-free
//   /client/payment-return/<surface> redirectUrl with the gateway. But the
//   redirect URL is frozen INTO THE GATEWAY ORDER at staking time, so every
//   checkout created before this code ships will still come back to
//   /client/wallet|invoices|bookings?payment=return&ref=… . Deleting this
//   branch would break the return leg for every payment in flight across the
//   deploy window. Remove it only once no pre-deploy checkout can still be
//   open (they hard-expire at 24h).
//   strips payment/ref from the address bar (history.replaceState) so
//   refresh/back can't re-trigger the result screen.
//
//   DEDICATED-PAGE mode — options.refOverride set (the query-free return
//   routes /client/payment-return/<surface>?ref=…): the caller owns the URL,
//   nothing is stripped (refresh re-verifies idempotently) and the login
//   round-trip preserves the page's own URL.
//
// Either way the hook then:
//   1. polls POST /api/payments/verify {order_ref} every 2s for up to 30s while
//      the gateway still reports the order as pending (verify is idempotent —
//      safe to poll; the page NEVER trusts the redirect itself, only the
//      verified outcome — PLAN_payments invariant);
//   2. exposes the outcome so the host page renders the shared
//      PaymentResultOverlay (or any custom UI).
//
// status: null | 'checking' | 'paid' | 'verified' | 'failed' | 'expired'
//         | 'still_pending' | 'not_found' | 'unauthenticated'
//   ('verified' = the card-verification rail's own success outcome (backend
//    aliases it 'card_verified'): the card was tokenised off a temporary AUTH
//    hold — NOTHING was captured, no wallet row exists. It is deliberately a
//    distinct state from 'paid' so no surface can present it as a payment;
//    the verify payload carries result.verification.message with the truthful
//    hold-released vs hold-standing line to show the client.
//    'failed' covers the gateway's failed AND cancelled outcomes — same client
//    message: nothing was charged. 'still_pending' = 30s elapsed without a
//    final state; the backend reconciliation poller will settle it.
//    'not_found' = /verify answered 404 — the ref does not belong to this
//    account (or is mangled): TERMINAL, polling would never resolve it.
//    'unauthenticated' = /verify answered 401 — the session cookie is missing
//    on THIS origin (e.g. paypage returned to localhost while the login lives
//    on 127.0.0.1, or the session expired mid-payment). The overlay shows a
//    calm "log in to see it" card instead of the app dumping the raw login
//    screen; loginUrl preserves the full return URL so verification re-runs
//    after login.)
// onPaid(data) fires once with the verify response on BOTH success outcomes —
//   'paid' and 'verified' ({ deposit_id?, request_id?, amount?, kind?,
//   purpose?, verification? }) — it is the "the checkout succeeded, refresh /
//   clean up" callback, and a verified card setup completes its flow (card
//   stored; on autopay_setup the staged booking request is filed) exactly
//   like a paid checkout does.
// result   = the raw verify response on 'paid' (amount/purpose feed the overlay).
// orderRef = the order ref being verified (feeds the overlay's retry/cancel
//            actions on failed/expired outcomes).
// loginUrl = /login?next=<return URL> (non-null whenever a ref was captured).

// Module-level "a payment return is being handled" flag. The global axios 401
// interceptor (App.js) consults this and SKIPS its logout + hard redirect to
// /login while a return is in flight — otherwise the very 401 we want to
// present calmly (plus the page's own parallel fetches failing with 401)
// would clear localStorage and dump the raw login screen mid-flow.
let paymentReturnActive = false;
export const isPaymentReturnActive = () => paymentReturnActive;

export default function usePaymentReturn({ onPaid, refOverride = null } = {}) {
    // Pure read — the URL is only mutated in the effect below. Raising the
    // module flag here (during render, before ANY data-fetch effects run) is
    // what keeps the interceptor from redirecting while we verify.
    const [orderRef] = useState(() => {
        if (refOverride) {
            paymentReturnActive = true;
            return refOverride;
        }
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('payment') === 'return' ? params.get('ref') : null;
        if (ref) paymentReturnActive = true;
        return ref;
    });
    const [status, setStatus] = useState(null);
    const [result, setResult] = useState(null);
    const onPaidRef = useRef(onPaid);
    onPaidRef.current = onPaid;

    useEffect(() => {
        if (!orderRef) return undefined;
        paymentReturnActive = true; // survive StrictMode's unmount/remount

        // LEGACY mode only: clean the query params straight away (idempotent
        // on re-runs). Dedicated return pages keep their URL — refresh simply
        // re-verifies, and the login round-trip needs the ref in place.
        if (!refOverride) {
            const params = new URLSearchParams(window.location.search);
            if (params.get('payment') === 'return') {
                params.delete('payment');
                params.delete('ref');
                const qs = params.toString();
                window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
            }
        }

        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        const POLL_MS = 2000;
        const MAX_MS = 30000;

        setStatus('checking');

        // The flag stays RAISED through terminal outcomes: the failed/expired
        // overlay's own "Try payment again" / "Cancel this payment" calls are
        // payment actions too, and a 401 on one of them must NOT clear
        // localStorage and hard-redirect to /login (no ?next=), destroying the
        // outcome screen and the order ref. It is lowered on dismiss and on
        // unmount (below) — i.e. once the client is finished with the outcome.
        const settle = (s) => {
            setStatus(s);
        };

        const poll = async () => {
            try {
                const res = await axios.post('/api/payments/verify', { order_ref: orderRef });
                if (cancelled) return;
                const outcome = res.data?.outcome;
                if (outcome === 'paid') {
                    setResult(res.data);
                    settle('paid');
                    if (onPaidRef.current) onPaidRef.current(res.data);
                    return;
                }
                // CARD VERIFICATION success (AUTH + verified void rail).
                // Terminal SUCCESS — the card is saved; nothing was captured.
                // Left unmapped this fell into "keep polling" and every
                // successful card verification ended on 'still_pending'.
                if (outcome === 'verified' || outcome === 'card_verified') {
                    setResult(res.data);
                    settle('verified');
                    if (onPaidRef.current) onPaidRef.current(res.data);
                    return;
                }
                if (outcome === 'failed' || outcome === 'cancelled') { setResult(res.data); settle('failed'); return; }
                if (outcome === 'expired') { setResult(res.data); settle('expired'); return; }
                // 'pending' (or anything unrecognised) → keep polling in the window.
                if (Date.now() - startedAt < MAX_MS) timer = setTimeout(poll, POLL_MS);
                else settle('still_pending');
            } catch (err) {
                if (cancelled) return;
                // No session on this origin — definitive, stop polling. The flag
                // stays RAISED so the interceptor keeps its hands off while the
                // overlay offers the login CTA.
                if (err.response?.status === 401) { setStatus('unauthenticated'); return; }
                // 404 — the ref doesn't belong to this account (or is mangled).
                // TERMINAL: polling for 30s and then claiming "we'll update your
                // account automatically" would be untrue.
                if (err.response?.status === 404) { settle('not_found'); return; }
                // Transient error — retry within the window rather than telling
                // the client anything definitive we haven't verified.
                if (Date.now() - startedAt < MAX_MS) timer = setTimeout(poll, POLL_MS);
                else settle('still_pending');
            }
        };
        poll();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            paymentReturnActive = false;
        };
    }, [orderRef, refOverride]);

    // Login link that round-trips back to THIS page with the payment params
    // restored, so the verification (idempotent) re-runs after login and the
    // client still gets a real, verified outcome. Dedicated return pages keep
    // their own URL (ref already in the query string).
    const loginUrl = orderRef
        ? `/login?next=${encodeURIComponent(
            refOverride
                ? `${window.location.pathname}${window.location.search}`
                : `${window.location.pathname}?payment=return&ref=${encodeURIComponent(orderRef)}`
        )}`
        : null;

    return {
        status,
        result,
        orderRef,
        loginUrl,
        // Lets the overlay's own retry/cancel actions fall back to the calm
        // "log in to continue" card on a 401 instead of a hard logout.
        setUnauthenticated: () => setStatus('unauthenticated'),
        dismiss: () => { paymentReturnActive = false; setStatus(null); },
    };
}
